import type { ChartType } from 'chart.js';

import { buildCsvContent } from '@/hooks/download-CSV-XLSX';
import { roundToDecimals } from '@/utils/localeFormatters';

import type { MetricXAxisKind } from './generic/axisTypes';

/** What the chart tells the CSV download about itself, under `options.plugins.csvExport`. */
export interface ChartCsvOptions {
    /** What the x keys are, which names the first column. Defaults to `'day'`. */
    xAxisKind?: MetricXAxisKind;
    /**
     * The x keys as the axis names them, index-aligned with the keys, for a category axis
     * whose keys are not words a reader knows (`2026-W31` shown as `week 31`).
     */
    xLabels?: string[];
}

declare module 'chart.js' {
    interface ChartDatasetProperties<TType extends ChartType, TData> {
        /**
         * Keeps a helper series (a fitted trend, the remainder of a stacked share) out of
         * "Download data as CSV", which holds only measured values.
         */
        excludeFromCsv?: boolean;
    }

    interface PluginOptionsByType<TType extends ChartType> {
        csvExport?: ChartCsvOptions;
    }
}

/** The part of a Chart.js chart the CSV needs; a `Chart` satisfies it. */
export interface ChartCsvSource {
    data: {
        labels?: unknown[];
        datasets: { label?: string; data: unknown[]; excludeFromCsv?: boolean }[];
    };
    isDatasetVisible(datasetIndex: number): boolean;
    /** Chart.js hands options back deep-partial, so a label may read as missing. */
    options: {
        plugins?: {
            csvExport?: { xAxisKind?: MetricXAxisKind; xLabels?: readonly (string | undefined)[] };
        };
    };
}

/**
 * Enough places for any value a chart plots, while dropping the float noise of a derived one
 * (0.1 * 3 is 0.30000000000000004).
 */
const CSV_DECIMALS = 6;

/**
 * A CSV field, quoted when it holds a separator, a quote or a line break. Text a spreadsheet
 * would run as a formula (a series named `=1+1` after a pen) gets a leading apostrophe, so the
 * cell shows the text instead. Only names and keys pass through here, never the numbers.
 */
export function csvField(value: string): string {
    const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
    return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function plottedValue(point: unknown): number | null {
    const value = typeof point === 'object' && point !== null && 'y' in point ? point.y : point;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function csvValue(point: unknown): string {
    const value = plottedValue(point);
    return value === null ? '' : String(roundToDecimals(value, CSV_DECIMALS));
}

/**
 * The chart's data as it is on screen, as CSV: one row per x key, one column per series drawn
 * (a series switched off in the legend is left out, and so are helper series). The first
 * column is named for the kind of x axis, from `xHeaders`. A gap is an empty cell, a number is
 * plain (`.` decimals, no grouping). The layout is the one the page exports use
 * (`buildCsvContent`).
 */
export function chartCsvContent(
    chart: ChartCsvSource,
    xHeaders: Record<MetricXAxisKind, string>
): string {
    const csvOptions = chart.options.plugins?.csvExport;
    const columns = chart.data.datasets
        .map((dataset, index) => ({ dataset, index }))
        .filter(({ dataset, index }) => !dataset.excludeFromCsv && chart.isDatasetVisible(index))
        .map(({ dataset }) => dataset);
    const keys = chart.data.labels ?? [];

    const header = [
        xHeaders[csvOptions?.xAxisKind ?? 'day'],
        ...columns.map((dataset) => dataset.label ?? ''),
    ].map(csvField);
    const rows = keys.map((key, index) => [
        csvField(csvOptions?.xLabels?.[index] ?? String(key)),
        ...columns.map((dataset) => csvValue(dataset.data[index])),
    ]);
    return buildCsvContent(header, rows);
}
